import React from 'react';
import {
  JobExecution,
  ACTIVE_STATUSES,
  ExecutionDetailResponseBody,
  ListExecutionsResponseBody,
} from '@lct-jobs/core';
import { useInterval } from './useInterval';

const POLL_INTERVAL_MS = 10000;
// A poll still pending after two intervals is treated as lost so polling can resume.
export const POLL_MAX_AGE_MS = POLL_INTERVAL_MS * 2;

type ExecutionData = ListExecutionsResponseBody | ExecutionDetailResponseBody;

function isExecutionActive(execution: JobExecution): boolean {
  if (ACTIVE_STATUSES.includes(execution.status as any)) {
    return true;
  }
  if (
    execution.status === 'PENDING_SCHEDULED' &&
    execution.scheduledStartTime &&
    new Date(execution.scheduledStartTime) <= new Date()
  ) {
    return true;
  }
  return false;
}

function hasActiveExecutions(data: ExecutionData): boolean {
  if ('executionId' in data) {
    return isExecutionActive(data as JobExecution);
  }

  const listData = data as ListExecutionsResponseBody;
  if (!listData.executions || listData.executions.length === 0) {
    return false;
  }

  return listData.executions.some(isExecutionActive);
}

export function useExecutionPolling(
  executionData: ExecutionData | undefined,
  refetch: () => unknown,
) {
  // Skips a tick while the previous poll is still running, so a slow API can't pile up requests.
  // Holds the start time so a refetch that never settles is abandoned after POLL_MAX_AGE_MS.
  const startedAt = React.useRef<number | null>(null);

  // useInterval keeps the timer stable across re-renders (executionData is a fresh ref each fetch).
  useInterval(() => {
    if (!executionData || !hasActiveExecutions(executionData)) return;
    if (startedAt.current !== null && Date.now() - startedAt.current < POLL_MAX_AGE_MS) return;
    const now = Date.now();
    startedAt.current = now;
    Promise.resolve(refetch())
      .catch(() => {})
      .finally(() => {
        if (startedAt.current === now) startedAt.current = null;
      });
  }, POLL_INTERVAL_MS);
}

export default useExecutionPolling;
